/**
 * Lightweight GD orchestration for Gemini Live WebSocket messages.
 *
 * Resilience features:
 * - Keepalive ping every 30s to prevent proxy/LB idle kills
 * - Auth token refresh on 1008 close instead of permanent give-up
 * - Exponential backoff reconnection (15 retries, 1s-30s, jitter)
 * - Visibility change handler to detect stale connections on tab focus
 * - onDisconnected callback so UI can show reconnecting state
 */

import StreamingAudioPlayer from './StreamingAudioPlayer';
import { API_CONFIG } from '../utils/constants';

export interface GdParticipant {
  id: string;
  name: string;
  type: 'moderator' | 'ai_speaker' | 'human';
  color?: string;
}

export interface GdMessage {
  speaker: string;
  speaker_name: string;
  text: string;
  timestamp: string;
}

export interface GdSessionInit {
  session_id: string;
  websocket_url?: string;
  topic?: string;
  topic_description?: string;
}

export interface GdOrchestrationCallbacks {
  onStatus: (status: string) => void;
  onParticipants: (participants: GdParticipant[]) => void;
  onCurrentSpeaker: (speaker: string | null) => void;
  onUserTurn: (isUserTurn: boolean) => void;
  onMessage: (message: GdMessage) => void;
  onRound: (round: number | null) => void;
  onDiscussionComplete: () => void;
  onConnected: () => void;
  onDisconnected: () => void;
  onError: (message: string) => void;
}

type IncomingMessage =
  | { type: 'discussion_status'; status?: { phase?: string; round?: number; total_rounds?: number; current_speaker?: string; participants?: GdParticipant[] } }
  | { type: 'turn_start'; speaker?: string; speaker_name?: string; round?: number; is_summary?: boolean }
  | { type: 'audio_chunk'; speaker?: string; speaker_name?: string; audio?: string; sample_rate?: number }
  | { type: 'text_response'; speaker?: string; speaker_name?: string; text?: string }
  | { type: 'turn_end'; speaker?: string; speaker_name?: string }
  | { type: 'round_complete'; round?: number }
  | { type: 'discussion_complete' }
  | { type: 'error'; message?: string }
  | Record<string, unknown>;

export class GdOrchestrationService {
  private ws: WebSocket | null = null;
  private callbacks: GdOrchestrationCallbacks;
  private audioPlayer: StreamingAudioPlayer;
  private sessionId: string | null = null;
  private lastSessionInit: GdSessionInit | null = null;
  private shouldReconnect = true;
  private connecting = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private hasReceivedMessage = false;
  private messageTimeout: ReturnType<typeof setTimeout> | null = null;
  private authRefreshInFlight = false;
  private lastServerMessageAt = 0; // Timestamp of last message received from server

  // -- Configurable resilience knobs --
  private readonly maxReconnects = 15;
  private readonly firstMessageTimeoutMs = 10000;
  private readonly pingIntervalMs = 20000; // 20s keepalive — two pings within any 60s proxy timeout window
  private readonly staleThresholdMs = 45000; // 45s without server message = stale, proactive reconnect
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  // -- Visibility change handler --
  private boundVisibilityHandler: (() => void) | null = null;

  constructor(callbacks: GdOrchestrationCallbacks) {
    this.callbacks = callbacks;
    this.audioPlayer = new StreamingAudioPlayer(
      (speaker) => this.callbacks.onCurrentSpeaker(speaker),
      () => this.callbacks.onCurrentSpeaker(null)
    );
  }

  // ─── Connection ────────────────────────────────────────────────────

  /**
   * Connect to GD WebSocket using query parameter authentication.
   */
  public connect(session: GdSessionInit, isReconnect: boolean = false): void {
    this.lastSessionInit = session;
    this.shouldReconnect = true;
    if (!isReconnect) {
      this.reconnectAttempts = 0;
    }

    // Prevent concurrent connects
    if (this.connecting) {
      return;
    }

    // Avoid reconnect if WS already open or connecting
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const authToken = localStorage.getItem('authToken');
    const path = session.websocket_url || `/ws/gd/${session.session_id}`;
    let wsUrl = this.buildWebSocketUrl(path);

    // Always use query parameter auth for dev server proxy compatibility
    if (authToken) {
      const separator = wsUrl.includes('?') ? '&' : '?';
      wsUrl = `${wsUrl}${separator}token=${encodeURIComponent(authToken)}`;
    }

    this.sessionId = session.session_id;
    this.callbacks.onStatus(isReconnect
      ? `🔄 Reconnecting... (attempt ${this.reconnectAttempts}/${this.maxReconnects})`
      : '🔌 Connecting to discussion...');
    this.connecting = true;

    try {
      console.log('🔌 Creating WebSocket:', { wsUrl: wsUrl.replace(/token=[^&]+/, 'token=***'), authTokenLength: authToken?.length });
      this.ws = new WebSocket(wsUrl);
      console.log('🔌 WebSocket created, readyState:', this.ws.readyState, '(0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED)');
    } catch (err) {
      console.error('Failed to open GD websocket', err);
      this.callbacks.onError('Failed to open GD connection');
      this.connecting = false;
      return;
    }

    this.ws.onopen = () => {
      console.log('🎉 WebSocket ONOPEN fired! ReadyState:', this.ws?.readyState);
      this.reconnectAttempts = 0;
      this.authRefreshInFlight = false;
      this.callbacks.onStatus('✅ Connected to discussion');
      this.callbacks.onConnected();
      this.hasReceivedMessage = false;
      this.lastServerMessageAt = Date.now();
      this.connecting = false;

      // Start keepalive ping
      this.startPing();

      // Install visibility handler
      this.installVisibilityHandler();

      // Set a timeout waiting for the first message
      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout);
      }
      this.messageTimeout = setTimeout(() => {
        if (!this.hasReceivedMessage) {
          console.warn('GD websocket: no message received within timeout, still waiting (no client close)');
          this.callbacks.onStatus(`⏳ No messages yet after ${this.firstMessageTimeoutMs / 1000}s, waiting...`);
        }
      }, this.firstMessageTimeoutMs);
    };

    this.ws.onmessage = (event) => {
      this.hasReceivedMessage = true;
      this.lastServerMessageAt = Date.now();
      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout);
        this.messageTimeout = null;
      }
      this.handleMessage(event.data);
    };

    this.ws.onerror = (err) => {
      console.error('GD websocket error', err);
      this.callbacks.onError('WebSocket error');
      this.connecting = false;
      this.scheduleReconnect();
    };

    this.ws.onclose = (event) => {
      this.connecting = false;
      this.stopPing();

      this.callbacks.onStatus(`🔌 Disconnected (code ${event.code || 'n/a'}, reason: ${event.reason || 'n/a'}, clean: ${event.wasClean})`);
      console.warn('GD websocket closed', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout);
        this.messageTimeout = null;
      }
      this.callbacks.onUserTurn(false);
      this.callbacks.onCurrentSpeaker(null);
      this.callbacks.onDisconnected();

      // Auth failure: refresh token and retry instead of permanent death
      if (event.code === 1008 || event.code === 1003) {
        this.handleAuthFailureAndReconnect();
        return;
      }

      // If we never received a message, limit reconnect churn to a single retry
      if (!this.hasReceivedMessage && this.reconnectAttempts >= 1) {
        this.shouldReconnect = false;
        this.callbacks.onError('WebSocket closed before first message; check backend reachability/auth');
        return;
      }

      this.scheduleReconnect();
    };
  }

  // ─── Auth failure recovery ─────────────────────────────────────────

  /**
   * On WS close with 1008/1003, attempt to refresh the auth token
   * via authClient before retrying the connection. Only one attempt
   * per disconnect cycle to avoid infinite loops.
   */
  private async handleAuthFailureAndReconnect(): Promise<void> {
    if (this.authRefreshInFlight) {
      console.warn('🔐 Auth refresh already in flight, skipping duplicate');
      this.callbacks.onError('WebSocket authentication failed (refresh already attempted)');
      return;
    }

    if (!this.lastSessionInit || !this.shouldReconnect) {
      this.callbacks.onError('WebSocket authentication failed');
      return;
    }

    this.authRefreshInFlight = true;
    this.callbacks.onStatus('🔄 Session expired, refreshing authentication...');
    console.log('🔐 [GD WS] Auth failure (1008/1003) — attempting token refresh before reconnect');

    try {
      // Dynamic import to avoid circular dependency
      const authClient = (await import('../api/authClient')).default;
      const refreshResponse = await authClient.post('/auth/refresh');
      const newToken = refreshResponse.data.access_token;
      const expiresIn = refreshResponse.data.expires_in;

      // Update stored token and expiry
      localStorage.setItem('authToken', newToken);
      const expiresAt = Date.now() + (expiresIn * 1000);
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());

      console.log('✅ [GD WS] Token refreshed successfully, reconnecting with fresh token...');
      this.callbacks.onStatus('✅ Token refreshed, reconnecting...');

      // Reconnect with the fresh token (connect reads from localStorage)
      this.connect(this.lastSessionInit, true);
    } catch (error) {
      console.error('❌ [GD WS] Token refresh failed:', error);
      this.authRefreshInFlight = false;
      this.shouldReconnect = false;
      this.callbacks.onError('WebSocket authentication failed — please refresh the page');
    }
  }

  // ─── Reconnection with exponential backoff ─────────────────────────

  private scheduleReconnect(): void {
    if (!this.shouldReconnect || !this.lastSessionInit) {
      return;
    }
    if (this.reconnectAttempts >= this.maxReconnects) {
      this.callbacks.onError('WebSocket disconnected (max retries reached)');
      this.shouldReconnect = false;
      return;
    }
    this.reconnectAttempts += 1;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    // Exponential backoff: min(1000 * 2^attempt, 30000) with jitter
    const baseDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
    const jitter = baseDelay * (0.5 + Math.random() * 0.5);
    const delay = Math.round(jitter);

    this.callbacks.onStatus(`🔄 Reconnecting in ${(delay / 1000).toFixed(1)}s... (attempt ${this.reconnectAttempts}/${this.maxReconnects})`);

    this.reconnectTimer = setTimeout(() => {
      if (!this.shouldReconnect || !this.lastSessionInit) return;
      console.log(`🔄 Reconnecting GD websocket... attempt ${this.reconnectAttempts}/${this.maxReconnects} (delay was ${delay}ms)`);
      this.connect(this.lastSessionInit, true);
    }, delay);
  }

  // ─── Keepalive ping ────────────────────────────────────────────────

  private startPing(): void {
    this.stopPing();
    console.log(`🏓 [GD WS] Starting keepalive ping every ${this.pingIntervalMs / 1000}s (stale threshold: ${this.staleThresholdMs / 1000}s)`);
    this.pingTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Check for stale connection: no server message for too long
        const silenceMs = Date.now() - this.lastServerMessageAt;
        if (this.hasReceivedMessage && silenceMs > this.staleThresholdMs) {
          console.warn(`🏓 [GD WS] Stale connection detected — no server message for ${(silenceMs / 1000).toFixed(1)}s (threshold: ${this.staleThresholdMs / 1000}s). Forcing reconnect.`);
          this.callbacks.onStatus('🔄 Connection stale, reconnecting...');
          this.callbacks.onDisconnected();
          // Close the stale socket; onclose will trigger reconnect
          try { this.ws.close(4000, 'Stale connection'); } catch (_) { /* ignore */ }
          return;
        }

        // Send keepalive ping
        try {
          this.ws.send(JSON.stringify({ type: 'ping' }));
          console.log(`🏓 [GD WS] Ping sent (last server msg ${(silenceMs / 1000).toFixed(1)}s ago)`);
        } catch (err) {
          console.warn('🏓 [GD WS] Ping send failed:', err);
        }
      } else {
        console.warn('🏓 [GD WS] Ping skipped — WS not open (readyState:', this.ws?.readyState, ')');
      }
    }, this.pingIntervalMs);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  // ─── Visibility change handler ─────────────────────────────────────

  private installVisibilityHandler(): void {
    this.removeVisibilityHandler();
    this.boundVisibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ [GD WS] Tab became visible, checking WebSocket health...');
        if (this.ws && this.ws.readyState !== WebSocket.OPEN && this.shouldReconnect && this.lastSessionInit) {
          console.log('👁️ [GD WS] WebSocket not open, triggering immediate reconnect');
          this.callbacks.onDisconnected();
          this.scheduleReconnect();
        }
      }
    };
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
  }

  private removeVisibilityHandler(): void {
    if (this.boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
      this.boundVisibilityHandler = null;
    }
  }

  // ─── User actions ──────────────────────────────────────────────────

  /**
   * Send user text turn.
   */
  public sendUserText(text: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.callbacks.onError('Connection not ready');
      return;
    }
    this.ws.send(JSON.stringify({ type: 'user_text', text }));
  }

  /**
   * Start recording - notify backend.
   */
  public startRecording(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.callbacks.onError('Connection not ready');
      return;
    }
    this.ws.send(JSON.stringify({ type: 'start_recording' }));
  }

  /**
   * Send user audio chunk (PCM 16-bit base64).
   */
  public sendAudioChunk(audioData: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.callbacks.onError('Connection not ready');
      return;
    }
    this.ws.send(JSON.stringify({ type: 'audio_chunk', audio_data: audioData }));
  }

  /**
   * Stop recording - notify backend to process accumulated chunks.
   */
  public stopRecording(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.callbacks.onError('Connection not ready');
      return;
    }
    this.ws.send(JSON.stringify({ type: 'stop_recording' }));
  }

  /**
   * Request stop and close.
   */
  public stop(): void {
    this.shouldReconnect = false;
    this.hasReceivedMessage = false;
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
      this.messageTimeout = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'stop' }));
    }
    this.cleanup();
  }

  /**
   * Prepare audio context (call on user gesture).
   */
  public async prepareAudio(): Promise<void> {
    await this.audioPlayer.prepare();
  }

  /**
   * Cleanup sockets, timers, and audio immediately.
   */
  public cleanup(): void {
    this.cleanupConnection();
    this.audioPlayer.cleanup();
  }

  /**
   * Graceful shutdown: close the WebSocket and timers, but let the audio
   * player drain its scheduled chunks before cleaning up. Used when the
   * discussion completes naturally so the last speaker isn't cut off.
   */
  public async gracefulShutdown(): Promise<void> {
    this.cleanupConnection();
    // Let all scheduled audio finish playing, then clean up
    await this.audioPlayer.drainAndCleanup();
  }

  /**
   * Close WebSocket, stop timers and visibility handler.
   * Does NOT touch the audio player.
   */
  private cleanupConnection(): void {
    this.shouldReconnect = false;
    this.hasReceivedMessage = false;
    this.stopPing();
    this.removeVisibilityHandler();
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
      this.messageTimeout = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch (_) {
        /* ignore */
      }
    }
    this.ws = null;
  }

  // ─── Helpers ───────────────────────────────────────────────────────

  private buildWebSocketUrl(url: string): string {
    if (url.startsWith('ws://') || url.startsWith('wss://')) {
      return url;
    }
    // Use direct connection to backend (same as AI Interview)
    const baseUrl = API_CONFIG.INTERVIEW_API_URL || API_CONFIG.BASE_URL || 'http://localhost:8000';
    try {
      const parsed = new URL(baseUrl);
      const wsProtocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = parsed.port ? `${parsed.hostname}:${parsed.port}` : parsed.hostname;
      const path = url.startsWith('/') ? url : `/${url}`;
      return `${wsProtocol}//${host}${path}`;
    } catch {
      // Fallback
      const path = url.startsWith('/') ? url : `/${url}`;
      return `ws://localhost:8000${path}`;
    }
  }

  private handleMessage(raw: any): void {
    let data: IncomingMessage;
    try {
      data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (err) {
      console.warn('Failed to parse GD message', raw);
      return;
    }

    switch (data.type) {
      case 'discussion_status': {
        const status = (data as any).status || {};
        const participants = Array.isArray(status.participants) ? status.participants : [];
        this.callbacks.onParticipants(participants);
        if (typeof status.current_speaker === 'string') {
          this.callbacks.onCurrentSpeaker(status.current_speaker);
        }
        if (typeof status.round === 'number') {
          this.callbacks.onRound(status.round);
        }
        this.callbacks.onStatus(`📋 ${status.phase || 'In progress'}`);
        break;
      }
      case 'turn_start': {
        const isUserTurn = (data as any).speaker === 'user';
        this.callbacks.onUserTurn(isUserTurn);
        const speakerName = typeof (data as any).speaker_name === 'string'
          ? (data as any).speaker_name
          : String((data as any).speaker ?? 'AI');
        this.callbacks.onCurrentSpeaker(speakerName);
        this.callbacks.onStatus(isUserTurn ? '🎤 Your turn' : `🔊 ${speakerName} is speaking`);
        break;
      }
      case 'recording_started': {
        this.callbacks.onStatus('🎤 Recording started');
        console.log('✅ Backend confirmed recording started');
        break;
      }
      case 'transcription_result': {
        const text = (data as any).text || '';
        this.callbacks.onStatus('✅ Transcription received');
        this.callbacks.onMessage({
          speaker: 'user',
          speaker_name: 'You',
          text,
          timestamp: new Date().toISOString(),
        });
        console.log('✅ Transcription result:', text);
        break;
      }
      case 'transcription_failed': {
        const message = (data as any).message || 'Transcription failed';
        this.callbacks.onError(`Transcription failed: ${message}`);
        console.error('❌ Transcription failed:', message);
        break;
      }
      case 'audio_chunk': {
        this.audioPlayer.enqueueChunk({
          data: (data as any).audio,
          speaker_id: (data as any).speaker,
          speaker_name: (data as any).speaker_name,
          sampleRate: (data as any).sample_rate,
        });
        break;
      }
      case 'text_response': {
        this.callbacks.onMessage({
          speaker: typeof (data as any).speaker === 'string'
            ? (data as any).speaker
            : String((data as any).speaker ?? 'ai'),
          speaker_name: typeof (data as any).speaker_name === 'string'
            ? (data as any).speaker_name
            : String((data as any).speaker ?? 'AI'),
          text: (data as any).text,
          timestamp: new Date().toISOString(),
        });
        break;
      }
      case 'turn_end': {
        if ((data as any).speaker === 'user') {
          this.callbacks.onUserTurn(false);
        }
        this.callbacks.onCurrentSpeaker(null);
        break;
      }
      case 'round_complete': {
        this.callbacks.onRound((data as any).round ?? null);
        this.callbacks.onStatus(`✅ Round ${(data as any).round} complete`);
        break;
      }
      case 'discussion_complete': {
        this.callbacks.onStatus('🎉 Discussion complete — finishing audio...');
        this.callbacks.onDiscussionComplete();
        // Graceful shutdown: close WS but let queued audio drain before cleanup
        this.gracefulShutdown().then(() => {
          this.callbacks.onStatus('🎉 Discussion complete');
          console.log('✅ Graceful shutdown complete — all audio played');
        });
        break;
      }
      case 'pong': {
        // Server acknowledged our keepalive ping — nothing to do
        break;
      }
      case 'error': {
        this.callbacks.onError((data as any).message || 'Unknown GD error');
        break;
      }
      default:
        console.log('ℹ️ Unhandled GD message', data);
    }
  }
}

export default GdOrchestrationService;
